/*
<purpose>
  Active-ticket reviewer: two server-side paginated buckets (Offen + Entschieden),
  tab-switched with shared filters and a sort dropdown + direction flip. Renders rows inline
  (server-filtered by useActivePagination — no client-side re-filter).
</purpose>
<dependencies>
  useActivePagination (server-side cursor paginated rows + counts for each bucket);
  useTicketManager (tm.* mutators for API calls + undo orchestration);
  smarttix.getTicket / getPiiIgnoreList / bulkArchive / bulkUnarchive (direct API);
  TicketDetailComponent, ReanalysisModal, shared components.
</dependencies>
<constraints>
  - Both hooks (offenHook + entschiedenHook) run concurrently; only the active tab's
    sentinel is mounted (avoids double-fetching the invisible bucket on scroll).
  - hook's local removeRow/patchRow give optimistic UI update without a full refetch.
  - After bulk auto-archive: hook.reset() reloads the paginated view.
  - Filters are shared across both buckets so switching tabs keeps context.
  - Per-row unread dot on Offen rows: DB-backed via useReadState (#6). Entschieden rows
    never show the dot. Opening a row calls markRead() optimistically.
  - The press (decide, then step; decision 2026-10-28): Genehmigen changes the mail, Nächste Mail
    changes what the clerk sees. A yes or a no shows at once (usePress, optimistic); the row KEEPS
    its place in Offen, selected, with the verdict's glyph where the unread dot sat and its text
    greyed; the after-press card rises above the pressed slot. The row leaves only when the clerk
    moves on (Nächste Mail, or another row picked): it fades (120 ms) and closes (200 ms), the tab
    counts tick and Entschieden's underline pulses as it closes. Rückgängig (30 s) re-opens it at its
    place, selected. No verdict toast; the archive toast is unchanged. No tab jump.
</constraints>
*/
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import TicketDetailComponent from './TicketDetail';
import ReanalysisModal from './ReanalysisModal';
import { openTicketPopout } from './PopoutShell';
import { BentoCard } from './BentoCard';
import { SelectionToggleIcon, SpreadsheetRowIcon, CircleIcon, SortDirectionIcon, PopoutIcon, CheckIcon, InsteadIcon } from './icons';
import { FilterPanel, formFilterCountOf } from './FilterPanel';
import { TicketRowMeta } from './TicketRowMeta';
import { HeaderIconButton } from './HeaderIconButton';
import { HandlingStripFace, primaryBandStyle, handlingTooltipText } from './HandlingStripFace';
import { ServedFace } from './ServedFace';
import { ArrivalAge } from './ArrivalAge';
import type { LifecycleBucket } from '../services/lifecycleBuckets';
import { ACTIVE_TAB_SHIMMER_WIDTH } from '../services/lifecycleBuckets';
import { onTicketMutated, announceTicketMutated } from '../services/ticketSync';
import { AutoBar, Lever } from './AutoButton';
import { antwortenAutoAction } from '../utils/autoAction';
import { InfoTooltip } from './InfoTooltip';
import { RueckgaengigToast } from './RueckgaengigToast';
import { isSettledStatus, decidedAtOf } from '../services/settledRow';
import { usePress } from '../hooks/usePress';
import { pressHeadingVerdict, type PressVerdict } from '../services/pressVerdict';
import { declineInsteadWord } from '../services/declineVerdict';
import type { AfterPressView } from './PressBar';
import { STALE_SUGGESTION_TOAST, isStaleSuggestionError } from '../services/staleSuggestion';
import { isTriageDecidable } from '../services/draftClaims';
import { CountUpNumber } from './CountUpNumber';
import { useSelectionMode } from '../hooks/useSelectionMode';
import { useActivePagination, ActiveFilters, ActiveSort, ActiveDir } from '../hooks/useActivePagination';
import { useTicketFacets } from '../hooks/useTicketFacets';
import { attributeFilterParams, withAttributeValues, hasAttributeFilter, type AttributeFilter } from '../services/ticketAttributes';
import { pillFilterParams } from '../services/searchFaceIds';
import { theme } from '../theme';
import { STATUS_GROUPS, SECONDARY_AXIS_MAX_VISIBLE } from '../categories';
import { POLLING_INTERVAL_MS, COPY_TOAST_MS } from '../timings';
import { copyToastMessage } from '../words';
import { smarttix } from '../services/api';
import { UserRoles, TicketDetail, TicketListItem, ReanalysisRequest, PiiIgnoreEntry, DraftKind, FlagId } from '../serverTypes';
import { formatBackendError } from '../utils/formatError';
import { describeExcelBatchAction } from '../utils/excelBatchAction';
import { PageType } from './NavBar';
import { useTicketManager } from '../context/TicketManagerContext';
import { useMaintenanceMode } from '../context/MaintenanceContext';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { listDetailFlex } from '../services/listDetailRatio';
import { useListDetailFlip } from '../context/ListDetailFlipContext';
import { DateRangeValue } from './DateRangeInput';
import { useReadState } from '../hooks/useReadState';
import { useTicketDetailRefresh, TRANSIENT_STATUSES } from '../hooks/useTicketDetailRefresh';

interface AntwortenSeiteProps {
  onNavigate: (page: PageType, params?: Record<string, string>) => void;
  initialTicket?: string | null;
  userRoles?: UserRoles | null;
  /** Days-since-{receivedAt|decidedAt} threshold for Auto-archive.
      0 = disabled; >0 = Auto button archives stale tickets. */
  autoArchiveDays: number;
  /** Demo-only: gates the "Simulierte Systemdaten" fill button in the scaffold editor. */
  simulateSystemData: boolean;
  // Tenant Eingangsweg visibility, threaded from App's single /config read (see TicketDetail).
  showSourceChannel?: boolean;
  // Tenant-wide inline-image counting rule, threaded from App's single /config read — a per-host
  // config read is forbidden, so this arrives as a prop like showSourceChannel above.
  attachmentsCountInline?: boolean;
}

const daysSince = (iso: string | null | undefined): number => {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.floor((Date.now() - t) / 86400000);
};

const COMPACT_BREAKPOINT = 900;
// The decided row's leave (the press, 2026-10-28): it fades, then closes; rows below glide up.
const LEAVE_FADE_MS = 120;
const LEAVE_CLOSE_MS = 200;
// The pane's cross-fade when the selection moves to another mail.
const PANE_FADE_MS = 180;

/** A row this page keeps in Offen on its own account: decided and still selected (`held`), or
 *  brought back by Rückgängig until the server lists it again (`returning`). Retained here, row and
 *  place, because the background poll replaces the list with the server's, which no longer (or not
 *  yet) carries it. `confirmedAt`/`reopenedAt` say from which read on the server counts it in
 *  Entschieden, so the tab counts can follow the LIST rather than the server (counts tick on leave). */
interface KeptRow {
  row: TicketListItem;
  index: number;
  state: 'held' | 'returning';
  verdict: PressVerdict;
  confirmedAt: number | null;
  reopenedAt: number | null;
  /** Closing out of Offen: the counts already tick. */
  leaving: boolean;
}
type RowMotion = { phase: 'fade' | 'close' | 'from' | 'open'; h: number };
const MORPH_TRANSITION = 'flex 0.45s cubic-bezier(0.4,0,0.2,1), box-shadow 180ms ease-out, transform 180ms ease-out';

// The two tabs this page owns are a fixed subset of the ONE bucket type (Task R2, part 4c,
// 2026-09-27) — 'uploads' lives on DashboardSeite's own Verlauf tab, never here.
type ActiveTab = Extract<LifecycleBucket, 'offen' | 'entschieden'>;

const AntwortenSeite: React.FC<AntwortenSeiteProps> = ({
  onNavigate, initialTicket, userRoles, autoArchiveDays, simulateSystemData, showSourceChannel,
  attachmentsCountInline,
}) => {
  const tm = useTicketManager();
  const cm = useCategoryMeta();
  const { isRead, markRead } = useReadState();
  // The server 423s every write during maintenance — a copy still reaches the clipboard, but the
  // credit is not attempted (same posture the letter flow's Kopieren already takes in TicketDetail).
  const maintenance = useMaintenanceMode();

  // ─── Selection / UI state ────────────────────────────
  const [activeTab, setActiveTab] = useState<ActiveTab>('offen');
  const sel = useSelectionMode();
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const pendingUuidRef = useRef<string | null>(initialTicket || null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const selectedTicketRef = useRef(selectedTicket);
  selectedTicketRef.current = selectedTicket;
  const [detailLoading, setDetailLoading] = useState(false);
  // A clicked row is being read while the pane still shows the previous mail (dimmed, inert) — the
  // pane never empties to a spinner between two mails; only a first selection loads from blank.
  const [detailStale, setDetailStale] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReanalysisModal, setShowReanalysisModal] = useState(false);
  const [piiIgnoreList, setPiiIgnoreList] = useState<PiiIgnoreEntry[]>([]);
  const [arrivalUuid, setArrivalUuid] = useState<string | null>(null);
  const [removingUuids, setRemovingUuids] = useState<Set<string>>(new Set());
  const [undoToast, setUndoToast] = useState<{ count: number; verb: string; uuids: string[] } | null>(null);
  const [infoToast, setInfoToast] = useState<string | null>(null);
  // One toast for BOTH copy shapes. `kind` is what tells them apart: absent = the letter flow's
  // whole-suggestion copy (undo = undoCopyApprove), present = that one triage draft (undo = release
  // of that kind's claim). Two separate toasts would be able to stack on the same screen.
  const [copyToast, setCopyToast] = useState<{ uuid: string; kind?: DraftKind } | null>(null);
  // The rows this page keeps in Offen (KeptRow), their leave/return motion, and the rows that left
  // within the undo window (so Rückgängig can open them back at their place).
  const [kept, setKept] = useState<Map<string, KeptRow>>(new Map());
  const keptRef = useRef(kept);
  keptRef.current = kept;
  const [motion, setMotion] = useState<Map<string, RowMotion>>(new Map());
  const departedRef = useRef<Map<string, KeptRow & { h: number }>>(new Map());
  const leaveTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>[]>>(new Map());
  // Bumped as a decided row closes out of Offen: Entschieden's underline pulses once.
  const [pulseKey, setPulseKey] = useState(0);
  // A detail read ahead of time (the next open row, while the card stands) and a ticket the page
  // already holds (the undo's reopened row): either is shown at once, without the loading spinner.
  const detailCacheRef = useRef<Map<string, TicketDetail>>(new Map());
  const presetRef = useRef<TicketDetail | null>(null);
  // A row re-analysed from its decided face: the pane stays on it through the transient status.
  const [heldUuid, setHeldUuid] = useState<string | null>(null);
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  // Tab change exits selection (selection is tab-scoped) — and drops the SELECTED ROW with it
  // (walk fix round 4, 2026-09-09). The two are the same rule and only the first half was written:
  // a selection belongs to the list it was made in, so a tab switch left Entschieden listed beside a
  // detail pane still reading an Offen row — a pane describing a ticket the list next to it does
  // not contain. Both halves now key off the same effect.
  const tabMountedRef = useRef(false);
  useEffect(() => {
    if (sel.mode) sel.exitMode();
    setSelectedUuid(null);
    setSelectedTicket(null);
    // The tab the clerk OPENS is re-read at once, silently (rows stay while it runs): its list may
    // be up to one poll older than the count the other tab just showed (B5 walk, 2026-10-27). Not
    // on mount — both lists' first reads are already under way then.
    if (!tabMountedRef.current) { tabMountedRef.current = true; return; }
    (activeTab === 'offen' ? offenHookRef : entschiedenHookRef).current.refresh();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Detail pop-out ─────────────────────────────────
  // Maximizing is a per-SECTION act inside TicketDetail now (Part C wave 2, 2026-09-09) — the
  // whole-pane stage this header used to raise is retired, so nothing overlay-shaped is left for
  // this page to own. The pop-out is unchanged: it hands the ticket to its own window.
  const popoutDetail = useCallback(() => {
    if (selectedUuid) openTicketPopout(selectedUuid);
  }, [selectedUuid]);

  // ─── Filters (shared across both buckets) ───────────
  // Newest upload first, for EVERY role — the same default the server resolves when no sort is sent,
  // and the same one UploadsSeite carries, so a clerk moving between the two lists sees one ordering.
  // ⚠ EVIDENCE, NOT PREFERENCE: the previous zone-first default was measured unintuitive in E2E
  // sessions with the client. Read that before "restoring" a hazard-first or activity-first default.
  // ⚠ AND IT IS ROLE-INDEPENDENT BY RULE, not by accident: nothing here may key a default off the
  // tenant's role or its classification schema. `zone` remains a lens the user chooses (see the sort
  // select below); a guard enforces the rule for every component
  // (src/__tests__/sortDefaultRoleIndependence.test.ts).
  const [sort, setSort] = useState<ActiveSort>('uploaded');
  const [dir, setDir] = useState<ActiveDir>('desc');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(id);
  }, [search]);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [reasonCodeFilter, setReasonCodeFilter] = useState<string[]>([]);
  // Classified-alongside ATTRIBUTE filters (wim vocabulary v7, 2026-09-13), keyed by SERVED
  // attribute name — which attributes exist is the tenant's decision, so the state is a record
  // rather than one useState per attribute. The page holds it verbatim and never reads a name of
  // its own: FilterPanel renders the sections the vocabulary declares, and ticketAttributes
  // translates the record into list params.
  const [attributeFilter, setAttributeFilter] = useState<AttributeFilter>({});
  // Sender-identity registry (P2, 2026-09-13), Task R2 — the „Absender" organisation filter's own
  // state, held separately from attributeFilter: a matched organisation is a registry row, not a
  // classified-alongside attribute, so it carries its own list param (senderOrganisationId) rather
  // than riding ticketAttributes' translation table.
  const [senderFilter, setSenderFilter] = useState<string[]>([]);
  // part 7, 2026-10-11: the pill filter — the „Warnhinweis" flags (OR within). Sent through pillFilterParams with the served search ids (searchFaceIds).
  const [flagFilter, setFlagFilter] = useState<FlagId[]>([]);
  // part 7 fix loop, 2026-10-12: the „Formular" facet — only rows carrying a recognised form.
  const [hasFormFilter, setHasFormFilter] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterUser, setFilterUser] = useState('');
  const [dateRange, setDateRange] = useState<DateRangeValue>({ from: null, to: null });

  const filters: ActiveFilters = useMemo(() => ({
    search: debouncedSearch,
    primaryAxis: categoryFilter,
    secondaryAxis: reasonCodeFilter,
    from: dateRange.from,
    to: dateRange.to,
    uploadedBy: filterUser,
    senderOrganisationId: senderFilter,
    ...attributeFilterParams(attributeFilter),
    ...pillFilterParams({ flagFilter, hasFormFilter, search: debouncedSearch }, cm),
  }), [debouncedSearch, categoryFilter, reasonCodeFilter, dateRange.from, dateRange.to, filterUser, senderFilter, attributeFilter, flagFilter, hasFormFilter, cm]);

  const hasActiveFilters = !!categoryFilter || reasonCodeFilter.length > 0 || !!search || !!filterUser || !!dateRange.from || !!dateRange.to || senderFilter.length > 0 || hasAttributeFilter(attributeFilter) || flagFilter.length > 0 || hasFormFilter;

  // ─── Filter facets (Task R2, sender-identity registry, 2026-09-13) ──
  // Backs BOTH FilterPanel's „Absender" organisation section (its row set + counts) and the count
  // span every generic attribute bubble now carries. Fetched against the SAME filters the visible
  // bucket's own list read uses (activeTab — a clerk on Genehmigt reviewing counts for Offen would
  // be reading the wrong tab's numbers). The debounce, the out-of-order guard, the never-clear rule
  // and the unsupported-backend flag all live in useTicketFacets (final review, item 4) — this page
  // states only WHICH bucket and WHICH filters it is asking about.
  const { facets, facetsUnsupported } = useTicketFacets(activeTab, {
    search: search || undefined,
    primaryAxis: categoryFilter || undefined,
    secondaryAxis: reasonCodeFilter.length > 0 ? reasonCodeFilter : undefined,
    senderOrganisationId: senderFilter.length > 0 ? senderFilter : undefined,
    from: dateRange.from || undefined,
    to: dateRange.to || undefined,
    uploadedBy: filterUser || undefined,
    ...attributeFilterParams(attributeFilter),
    ...pillFilterParams({ flagFilter, hasFormFilter, search }, cm),
  });

  // ─── Paginated hooks (one per bucket) ───────────────
  const offenHook = useActivePagination('offen', sort, filters, dir);
  const entschiedenHook = useActivePagination('entschieden', sort, filters, dir);

  const offenRows = useMemo(() => {
    if (kept.size === 0) return offenHook.rows;
    // A held row is the page's own version (it carries the verdict) even while the server's list
    // still lists the open one: the press must read decided on the row at once, not at the next read.
    const rows = offenHook.rows.map(r => (kept.get(r.uuid)?.state === 'held' ? kept.get(r.uuid)!.row : r));
    [...kept.entries()].sort((a, b) => a[1].index - b[1].index).forEach(([uuid, { row, index }]) => {
      if (!rows.some(r => r.uuid === uuid)) rows.splice(Math.min(index, rows.length), 0, row);
    });
    return rows;
  }, [offenHook.rows, kept]);
  const entschiedenRows = entschiedenHook.rows;
  const activeHook = activeTab === 'offen' ? offenHook : entschiedenHook;
  // ⚠ THE ACTIVE HOOK'S counts, not offenHook's. The old comment here said "counts come from either
  // hook — they carry the same server-side counts object", and that is FALSE as written: the payloads
  // agree only when both fetches SUCCEED. Measured by the pre-merge reviewer: with the offen fetch
  // rejected and the entschieden fetch holding 5 form-bearing tickets, reading offenHook told the clerk
  // on the Entschieden tab that their view contained none. Reading the hook whose bucket is on screen
  // keeps the counts, the loading flag and the error from three different sources in agreement.
  // ⚠ And of the two reads, the NEWER one (B5 walk, 2026-10-27). Every read carries the counts of
  // all buckets, but the two lists are re-read at different moments: after a press only Entschieden
  // is re-read, so Offen's tab said 23 / 3 for four seconds; on a tab switch the other list's older
  // read said „(0)" while its list was loading; and a tab could say (3) above a list from an older
  // read. The newest successful read is the truth for both tabs. A failed read never stamps, so the
  // reviewer's case above (one fetch rejected) still reads the surviving one.
  const serverCounts = offenHook.countsAt >= entschiedenHook.countsAt ? offenHook.counts : entschiedenHook.counts;
  // ⚠ And the LIST's truth over the server's while this page keeps a row (the press, 2026-10-28:
  // the counts tick when the row leaves Offen, never at the press — "the count and the list never
  // disagree"). A kept row the newest read already counts in Entschieden is moved back to Offen
  // here, until it starts closing out of the list.
  const countsAt = Math.max(offenHook.countsAt, entschiedenHook.countsAt);
  let keptShift = 0;
  kept.forEach(k => {
    if (k.leaving || k.confirmedAt === null || countsAt < k.confirmedAt) return;
    if (k.reopenedAt !== null && countsAt >= k.reopenedAt) return;
    keptShift += 1;
  });
  const counts = keptShift === 0 ? serverCounts
    : { ...serverCounts, offen: serverCounts.offen + keptShift, entschieden: Math.max(0, serverCounts.entschieden - keptShift) };
  const activeRows = activeTab === 'offen' ? offenRows : entschiedenRows;

  // ─── Responsive ─────────────────────────────────────
  const [isCompact, setIsCompact] = useState(window.innerWidth < COMPACT_BREAKPOINT);
  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ─── PII ignore list ────────────────────────────────
  useEffect(() => {
    smarttix.getPiiIgnoreList().then(setPiiIgnoreList);
  }, []);

  // ─── Background poll (silent refresh) ───────────────
  // Fixes reanalysis "hang": after patchRow sets status='reanalyzing', the BE
  // finishes in ~20s and the row returns to suggestion_ready — without a poll
  // the row would show reanalyzing forever. refresh() never clears rows or flips loading.
  // Stable refs avoid recreating the interval when hook objects change each render.
  const offenHookRef = useRef(offenHook);
  const entschiedenHookRef = useRef(entschiedenHook);
  offenHookRef.current = offenHook;
  entschiedenHookRef.current = entschiedenHook;
  useEffect(() => {
    const id = setInterval(() => {
      offenHookRef.current.refresh();
      entschiedenHookRef.current.refresh();
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Foreign-window mutations: silent list refresh + detail refetch when it's ours.
  const selectedUuidRef = useRef(selectedUuid);
  selectedUuidRef.current = selectedUuid;
  useEffect(() => onTicketMutated(async (uuid) => {
    offenHookRef.current.refresh();
    entschiedenHookRef.current.refresh();
    const sel = selectedUuidRef.current;
    if (sel && (uuid === sel || uuid === '*')) {
      const detail = await smarttix.getTicket(sel);
      // Guard: selection may have moved on to a different ticket while this
      // getTicket() was in flight — don't clobber the NOW-selected ticket's
      // detail with a stale response for the ticket the user left behind.
      if (selectedUuidRef.current === sel && detail) setSelectedTicket(detail);
    }
  }), []);

  // The ONE guarded detail refetch every triage post-effect on this page uses (2026-09-07) — the
  // same idiom the foreign-mutation listener above already applied, and for the same two reasons:
  // the selection can move on while the read is in flight (a late answer for the ticket the clerk
  // LEFT must not overwrite the one they are now looking at), and a detail read that 404s or fails
  // must stay a no-op rather than escape as an unhandled rejection out of a handler nobody awaits.
  const refetchSelectedDetail = async (uuid: string) => {
    try {
      const detail = await smarttix.getTicket(uuid);
      if (selectedUuidRef.current === uuid && detail) setSelectedTicket(detail);
    } catch { /* a detail that cannot be re-read leaves the pane on what it already has */ }
  };

  const handlePiiIgnore = async (fieldType: string, value: string) => {
    await smarttix.addPiiIgnore(fieldType, value);
    setPiiIgnoreList(prev => [...prev, { id: 0, fieldType, value }]);
  };

  // ─── Detail loading ──────────────────────────────────
  // A ticket the page already holds is shown at once — the undo's reopened row (preset; the server
  // may not have reopened it yet, so no re-read races it: the undo re-reads when it is answered) or
  // the next row read ahead while the card stood (cached; re-read silently). Anything else loads.
  useEffect(() => {
    if (!selectedUuid) { setSelectedTicket(null); return; }
    let cancelled = false;
    const preset = presetRef.current?.uuid === selectedUuid ? presetRef.current : null;
    presetRef.current = null;
    if (preset) { setSelectedTicket(preset); setDetailLoading(false); return; }
    const cached = detailCacheRef.current.get(selectedUuid);
    detailCacheRef.current.delete(selectedUuid);
    if (cached) { setSelectedTicket(cached); setDetailLoading(false); }
    else if (selectedTicketRef.current) setDetailStale(true);
    else setDetailLoading(true);
    smarttix.getTicket(selectedUuid).then(detail => {
      if (cancelled) return;
      if (detail || !cached) setSelectedTicket(detail);
      setDetailLoading(false);
      setDetailStale(false);
    }).catch(() => { if (!cancelled) { setDetailLoading(false); setDetailStale(false); } });
    return () => { cancelled = true; };
  }, [selectedUuid]);

  // The pane cross-fades when the selection moves from one mail to another.
  const paneUuidRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = paneUuidRef.current;
    paneUuidRef.current = selectedTicket?.uuid ?? null;
    if (prev && selectedTicket && prev !== selectedTicket.uuid) {
      detailRegionRef.current?.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: PANE_FADE_MS, easing: 'ease-out' });
    }
  }, [selectedTicket?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Transient-status detail refresh ────────────────
  useTicketDetailRefresh(selectedTicket, setSelectedTicket);

  // ─── URL-param ticket resolution ─────────────────────
  // Tries loaded rows first; if not there yet, waits for rows to update.
  useEffect(() => {
    if (!pendingUuidRef.current) return;
    const uuid = pendingUuidRef.current;
    // A linked row that is DECIDED lives on the Entschieden tab: open that tab first, then select it
    // (the Dashboard's Verlauf card links Entschieden rows here, 2026-10-27). The tab switch clears
    // the selection, so the pending uuid is kept and resolved on the next pass, with the tab on screen.
    if (activeTab !== 'entschieden' && entschiedenRows.some(t => t.uuid === uuid) && !offenRows.some(t => t.uuid === uuid)) {
      setActiveTab('entschieden');
      return;
    }
    const inRows = [...offenRows, ...entschiedenRows].find(t => t.uuid === uuid);
    if (inRows) {
      pendingUuidRef.current = null;
      setSelectedUuid(uuid);
      setArrivalUuid(uuid);
      setTimeout(() => setArrivalUuid(null), 1600);
    }
    // else: rows not loaded yet; effect re-runs when rows change
  }, [offenRows, entschiedenRows, activeTab]);

  // ─── Clear stale selection ───────────────────────────
  // Guard: don't clear while detail pane tracks the ticket through a transient status.
  // selectedTicket (kept fresh by useTicketDetailRefresh) is the canonical liveness source.
  // Genuine orphans (non-transient status, or uuid mismatch) still clear immediately.
  useEffect(() => {
    if (selectedUuid && !offenHook.loading && !entschiedenHook.loading) {
      const stillInRows = [...offenRows, ...entschiedenRows].find(t => t.uuid === selectedUuid);
      const detailAlive = selectedTicket?.uuid === selectedUuid && TRANSIENT_STATUSES.has(selectedTicket.status);
      // A decided or returning row this page keeps is in offenRows already; a row re-analysed from
      // its decided face stays too (heldUuid), until the clerk selects another row.
      const settledAlive = heldUuid === selectedUuid;
      if (!stillInRows && !detailAlive && !settledAlive) setSelectedUuid(null);
    }
  }, [offenRows, entschiedenRows, selectedUuid, offenHook.loading, entschiedenHook.loading, selectedTicket, heldUuid]);
  // A returning row stops being kept once the server lists it in Offen again.
  useEffect(() => {
    const back = [...kept.entries()].filter(([uuid, k]) => k.state === 'returning' && offenHook.rows.some(r => r.uuid === uuid));
    if (back.length === 0) return;
    setKept(prev => { const next = new Map(prev); back.forEach(([uuid]) => next.delete(uuid)); return next; });
  }, [offenHook.rows, kept]);
  // The hold is the selection's: selecting another row (or none) releases it.
  useEffect(() => {
    if (heldUuid && selectedUuid !== heldUuid) setHeldUuid(null);
  }, [selectedUuid, heldUuid]);

  // ─── Actions ─────────────────────────────────────────

  // ─── The press (decide, then step; decision 2026-10-28) ───
  // The press itself (optimistic verdict, card, server call, rollback, undo window) is usePress,
  // shared with the pop-out. What this page adds is the LIST: the decided row keeps its place,
  // selected; it leaves when the clerk moves on; Rückgängig opens it back; the counts follow.

  /** The next open row from `uuid`: below it, else above it; null when none is left. */
  const nextOpenFrom = (uuid: string): string | null => {
    const rows = offenRows;
    const at = rows.findIndex(r => r.uuid === uuid);
    const open = (r: TicketListItem) => r.uuid !== uuid && keptRef.current.get(r.uuid)?.state !== 'held' && !motion.has(r.uuid);
    for (let n = at + 1; n < rows.length; n++) if (open(rows[n])) return rows[n].uuid;
    for (let n = at - 1; n >= 0; n--) if (open(rows[n])) return rows[n].uuid;
    return at < 0 ? (rows.find(open)?.uuid ?? null) : null;
  };

  const trackLeaveTimer = (uuid: string, t: ReturnType<typeof setTimeout>) => {
    const list = leaveTimersRef.current.get(uuid) ?? [];
    leaveTimersRef.current.set(uuid, [...list, t]);
  };
  const cancelLeave = (uuid: string) => {
    (leaveTimersRef.current.get(uuid) ?? []).forEach(clearTimeout);
    leaveTimersRef.current.delete(uuid);
  };
  const setRowMotion = (uuid: string, m: RowMotion | null) =>
    setMotion(prev => { const next = new Map(prev); if (m) next.set(uuid, m); else next.delete(uuid); return next; });

  /** The decided row leaves Offen: it fades, then closes; the counts tick and Entschieden pulses
   *  as it closes; then the list lets go of it (kept for Rückgängig as `departed`). */
  const startLeave = (uuid: string) => {
    const k = keptRef.current.get(uuid);
    if (!k || k.state !== 'held' || k.leaving) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-uuid="${uuid}"]`);
    const h = el?.offsetHeight ?? 0;
    setRowMotion(uuid, { phase: 'fade', h });
    trackLeaveTimer(uuid, setTimeout(() => {
      setRowMotion(uuid, { phase: 'close', h });
      setKept(prev => { const cur = prev.get(uuid); if (!cur) return prev; const next = new Map(prev); next.set(uuid, { ...cur, leaving: true }); return next; });
      setPulseKey(n => n + 1);
      trackLeaveTimer(uuid, setTimeout(() => {
        leaveTimersRef.current.delete(uuid);
        const cur = keptRef.current.get(uuid);
        if (cur) departedRef.current.set(uuid, { ...cur, h });
        setKept(prev => { const next = new Map(prev); next.delete(uuid); return next; });
        setRowMotion(uuid, null);
        offenHook.removeRow(uuid);
        offenHook.refresh();
        entschiedenHook.refresh();
      }, LEAVE_CLOSE_MS));
    }, LEAVE_FADE_MS));
  };
  useEffect(() => () => { leaveTimersRef.current.forEach(list => list.forEach(clearTimeout)); }, []);

  /** Rückgängig: the row is back at its place, selected, the pane on its open face. */
  const reopenRow = (uuid: string, reopened: TicketDetail) => {
    cancelLeave(uuid);
    const k = keptRef.current.get(uuid);
    if (k) {
      setKept(prev => new Map(prev).set(uuid, { ...k, row: { ...k.row, status: 'suggestion_ready' }, state: 'returning', leaving: false }));
      setRowMotion(uuid, null);
    } else {
      const d = departedRef.current.get(uuid);
      departedRef.current.delete(uuid);
      if (d) {
        setKept(prev => new Map(prev).set(uuid, { row: { ...d.row, status: 'suggestion_ready' }, index: d.index, state: 'returning', verdict: d.verdict, confirmedAt: d.confirmedAt, reopenedAt: null, leaving: false }));
        setRowMotion(uuid, { phase: 'from', h: d.h });
        requestAnimationFrame(() => requestAnimationFrame(() => setRowMotion(uuid, { phase: 'open', h: d.h })));
        trackLeaveTimer(uuid, setTimeout(() => setRowMotion(uuid, null), LEAVE_CLOSE_MS + 40));
      }
    }
    // The row is selected only when Offen is the tab on screen: a selection belongs to the list it
    // was made in, and no tab jumps.
    if (activeTab === 'offen') {
      if (selectedUuidRef.current === uuid) setSelectedTicket(reopened);
      else { presetRef.current = reopened; setSelectedUuid(uuid); }
    }
  };

  const press = usePress({
    patchTicket: (uuid, fn) => setSelectedTicket(prev => (prev && prev.uuid === uuid ? fn(prev) : prev)),
    refetchTicket: uuid => refetchSelectedDetail(uuid),
    onStale: uuid => { void handleStale(uuid); },
    onPressed: (uuid, verdict, decided) => {
      const index = offenRows.findIndex(r => r.uuid === uuid);
      // The held row reads decided at once: the verdict's status, and the approved letters' copy
      // claims dropped — core upgrades them to the approval, so a copied letter's „Kopiert" gives
      // way to the yes the pane already shows. (No approval claim is added: the held row wears the
      // check glyph, not the badge.)
      const approvedNow = Object.entries(decided.suggestion?.claims ?? {}).filter(([, c]) => c?.via === 'approve').map(([k]) => k);
      if (index >= 0) {
        const r = offenRows[index];
        const via = Object.fromEntries(Object.entries(r.draftClaimVia ?? {}).filter(([k]) => !approvedNow.includes(k)));
        const heldRow: TicketListItem = {
          ...r, status: verdict === 'approved' ? 'approved' : 'declined',
          draftClaims: (r.draftClaims ?? []).filter(k => !approvedNow.includes(k)),
          draftClaimVia: Object.keys(via).length ? via : null,
        };
        setKept(prev => new Map(prev).set(uuid, { row: heldRow, index, state: 'held', verdict, confirmedAt: null, reopenedAt: null, leaving: false }));
      }
      // The verdict's card is the one that speaks now: a copy toast or an archive undo still
      // standing in the corner would undo another act (B5 walk, 2026-10-27).
      setCopyToast(null);
      setUndoToast(null);
      // The next open row, read ahead: Nächste Mail then shows it without a loading spinner.
      const next = nextOpenFrom(uuid);
      if (next && !detailCacheRef.current.has(next)) {
        smarttix.getTicket(next).then(d => { if (d) detailCacheRef.current.set(next, d); }).catch(() => {});
      }
    },
    onConfirmed: uuid => {
      const at = Date.now();
      setKept(prev => { const cur = prev.get(uuid); if (!cur) return prev; return new Map(prev).set(uuid, { ...cur, confirmedAt: at }); });
      const d = departedRef.current.get(uuid);
      if (d) departedRef.current.set(uuid, { ...d, confirmedAt: at });
      offenHook.refresh();
      entschiedenHook.refresh();
    },
    onRefused: uuid => {
      cancelLeave(uuid);
      setRowMotion(uuid, null);
      departedRef.current.delete(uuid);
      setKept(prev => { if (!prev.has(uuid)) return prev; const next = new Map(prev); next.delete(uuid); return next; });
      offenHook.refresh();
    },
    onUndo: reopenRow,
    // The second WIM letter, approved from Entschieden: that row's badges change there.
    onLaterLetter: () => { entschiedenHook.refresh(); },
    onUndoSettled: (uuid, ok) => {
      if (ok) {
        const at = Date.now();
        setKept(prev => { const cur = prev.get(uuid); if (!cur) return prev; return new Map(prev).set(uuid, { ...cur, reopenedAt: at }); });
        void refetchSelectedDetail(uuid);
      } else {
        // A refused undo (the row moved on under the clerk): the list re-read decides where it is.
        setKept(prev => { if (!prev.has(uuid)) return prev; const next = new Map(prev); next.delete(uuid); return next; });
      }
      offenHook.refresh();
      entschiedenHook.refresh();
    },
  });
  const pressRef = useRef(press);
  pressRef.current = press;

  // Moving on — Nächste Mail, another row picked, the selection closed, a tab switch — is what
  // lets a decided row leave; the card collapses to its undo line.
  const prevSelectedRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevSelectedRef.current;
    prevSelectedRef.current = selectedUuid;
    if (!prev || prev === selectedUuid) return;
    if (pressRef.current.press?.uuid === prev) pressRef.current.toLine();
    if (keptRef.current.get(prev)?.state === 'held') startLeave(prev);
  }, [selectedUuid]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Nächste Mail: the next open row (below, else above) is selected; the decided row leaves. */
  const handleNext = () => {
    const p = press.press;
    if (!p) return;
    press.toLine();
    setSelectedUuid(nextOpenFrom(p.uuid));
  };

  // The card as this page shows it: full over the pressed mail; once the clerk moved on, the line.
  const afterPress: AfterPressView | null = press.press ? {
    uuid: press.press.uuid,
    mode: press.press.mode === 'full' && press.press.uuid === selectedUuid ? 'full' : 'line',
    verdictText: pressHeadingVerdict({
      verdict: press.press.verdict, lastKind: press.press.lastKind,
      declineWord: declineInsteadWord(cm, press.press.decline), statesKind: cm.statesApprovedKind,
    }, cm.face),
    subject: press.press.subject,
    side: press.press.side,
    undoLive: press.undoLive,
    onNext: activeTab === 'offen' ? handleNext : undefined,
    onUndo: () => { void press.undo(); },
    onStay: press.toLine,
  } : null;

  // Copy-approval: ticket stays in Offen, only copyApprovedAt is set.
  const handleCopyApprove = async () => {
    if (!selectedUuid) return;
    const uuid = selectedUuid;
    const result = await tm.copyApproveTicket(uuid);
    if (result.success) {
      offenHook.patchRow(uuid, { copyApprovedAt: new Date().toISOString() });
      setCopyToast({ uuid });
    } else {
      setError(result.error || 'Fehler beim Kopieren');
    }
  };

  // The row moved on under the clerk (Part C, 2026-09-07). ONE reaction for every write that can
  // lose that race — the claim behind Genehmigen, a copy claim, a release, a render, a scaffold
  // save: refetch the detail this page is showing and say, once, that it was reloaded. Never the
  // error banner: the remedy already ran, and "try again" would only lose the race twice.
  // The LIST is refreshed too (2026-09-07): a claim losing this race usually means the row was
  // settled elsewhere, so it has left Offen — reloading only the detail would leave the list
  // showing a row that is no longer there until the next poll tick.
  const handleStale = async (uuid: string) => {
    offenHook.refresh();
    await refetchSelectedDetail(uuid);
    setInfoToast(STALE_SUGGESTION_TOAST);
  };

  /**
   * Patch a row wherever it lives (round-6 review, 2026-09-09). Both copy-marker writes named
   * `offenHook` unconditionally, which was correct only while the copy guard forced
   * `suggestion_ready`: such a row can only be in Offen. Round 6 widened that guard to the decidable
   * statuses, so a copy can now land on an approved/sent row — and those are `entschiedenHook`'s,
   * where a patch aimed at Offen is a silent no-op. The badge then waited for the next poll, and an
   * undo could leave a stale one standing.
   * BOTH hooks, not a branch on the ticket's status: a status branch would re-derive "which list
   * holds this row" here, a second copy of the bucket rule that already lives in the two hooks' own
   * queries — and that rule has moved before (the BE bucket SSOT admitting mid-flight statuses into
   * Offen). A row is in exactly one list, `patchRow` leaves a uuid it does not hold untouched, so
   * this is correct without the page knowing which bucket that is.
   */
  /** A row's claim fields with one kind's claim set (or released, `null`) — read off whichever list
   *  holds the row, for the optimistic patch above. */
  const withRowClaim = (uuid: string, kind: DraftKind, via: 'copy' | null): Partial<TicketListItem> => {
    const row = [...offenHook.rows, ...entschiedenHook.rows].find(r => r.uuid === uuid);
    const claims = (row?.draftClaims ?? []).filter(k => k !== kind);
    const viaMap = { ...(row?.draftClaimVia ?? {}) };
    delete viaMap[kind];
    if (via) { claims.push(kind); viaMap[kind] = via; }
    return { draftClaims: claims, draftClaimVia: Object.keys(viaMap).length ? viaMap : null };
  };
  const patchRowInBothBuckets = (uuid: string, patch: Partial<TicketListItem>) => {
    offenHook.patchRow(uuid, patch);
    entschiedenHook.patchRow(uuid, patch);
  };

  // Copying ONE triage draft (Part C, 2026-09-07). The clipboard write is unconditional —
  // it is what the clerk asked for and it never depends on the server. The CLAIM is the credit, and
  // it is only owed while the row is the clerk's to settle: an already-settled row, maintenance, or
  // a kind that already carries a claim are all clipboard-only. That last one is not politeness:
  // the server answers stale_rejected for a second claim on the same kind, so asking would turn a
  // successful copy into a red banner. Like the letter flow, this NEVER moves the ticket.
  // Returns whether the CLIPBOARD write succeeded — the card confirms itself only on true, so a
  // browser that refused the write leaves the button saying Kopieren while this banner carries the
  // reason. Everything after the write is credit, not copying: its own failures are reported here
  // and never take the confirmation away from a copy that demonstrably happened.
  const handleCopyDraft = async (kind: DraftKind, text: string): Promise<boolean> => {
    // The card awaits this handler only for that boolean; nothing else does, so a rejected
    // clipboard write would otherwise escape as an unhandled rejection with the clerk told nothing.
    try { await navigator.clipboard.writeText(text); }
    catch { setError('Fehler beim Kopieren'); return false; }
    if (!selectedTicket || !selectedUuid) return true;
    const suggestion = selectedTicket.suggestion;
    // The statuses where a triage decision is still LIVE — not `suggestion_ready` alone (operator
    // ruling, walk fix round 6, 2026-09-09). A row in Genehmigt is settled about ONE kind; the
    // other draft is still a decision, and copying it is how the clerk takes it. Core never
    // disagreed — its executor has no ticket-status gate, only single-use per kind — so this guard
    // was the only thing turning that copy into a silent clipboard write. Archived rows and
    // maintenance still refuse: one has left the decision surface, the other pauses every write.
    if (!suggestion || !isTriageDecidable(selectedTicket.status) || maintenance) return true;
    if (suggestion.claims?.[kind]) return true;
    const uuid = selectedUuid;
    setError(null);
    const result = await tm.claimDraft(uuid, suggestion.id, kind, 'copy');
    // Stale FIRST: the clipboard already holds what the clerk asked for, so a red "Fehler beim
    // Kopieren" would be untrue as well as unactionable. No Rückgängig toast either — no credit
    // was recorded, so there is nothing to take back.
    if (isStaleSuggestionError(result)) { await handleStale(uuid); return true; }
    if (result.success) {
      // The row marker is a COPY-APPROVAL, and a receipt is not one (R31/R32, 2026-09-18): core
      // stopped stamping copy_approved_at for an ack copy, so stamping it optimistically here would
      // be the FE re-deriving the exact fact the backend deliberately stopped asserting. The
      // receipt's own signal („Eingang bestätigt") rides draftClaims, which the detail refetch
      // below brings back. Every other kind still earns the marker on the spot.
      // The copy claim rides along with the stamp: „Kopiert" reads the letter's own claim on a
      // triage row (settledRow.showsKopiert), so a stamp alone would not mark a decided row.
      if (kind !== 'ack') patchRowInBothBuckets(uuid, { copyApprovedAt: new Date().toISOString(), ...withRowClaim(uuid, kind, 'copy') });
      setCopyToast({ uuid, kind });
      await refetchSelectedDetail(uuid);
    } else {
      setError(result.error || 'Fehler beim Kopieren');
    }
    return true;
  };

  // Undo of whichever copy the toast is showing. Only the server call differs, and the toast's
  // `kind` is the whole discriminator.
  //
  // The ROW MARKER is not simply reverted with it. A copy claim leaves the row in Offen, so a
  // two-draft row can carry a copy claim on BOTH kinds; releasing one of them leaves the other
  // standing, and the server keeps copy_approved_at for it. Clearing the badge there would tell the
  // clerk the credit is gone while it demonstrably is not. So the marker clears only when no OTHER
  // copy claim survives the release — read off the claims the detail already carries, minus the
  // kind being released. The letter flow has no per-kind claims at all: its undo always clears.
  const handleUndoCopy = async () => {
    const toast = copyToast;
    setCopyToast(null);
    if (!toast) return;
    const result = toast.kind
      ? await tm.unclaimDraft(toast.uuid, toast.kind)
      : await tm.undoCopyApprove(toast.uuid);
    if (isStaleSuggestionError(result)) { await handleStale(toast.uuid); return; }
    if (!result.success) {
      setError(result.error || 'Fehler beim Zurücknehmen');
      return;
    }
    const claims = selectedTicket?.uuid === toast.uuid ? selectedTicket.suggestion?.claims : undefined;
    const anotherCopyClaimStands = !!toast.kind && !!claims && Object.entries(claims)
      .some(([claimed, claim]) => claimed !== toast.kind && claim?.via === 'copy');
    // Symmetrically: releasing a receipt never earned the marker, so it never clears one either —
    // a row whose forward copy still stands must not lose its Kopiert because the ack went back.
    if (toast.kind && toast.kind !== 'ack') patchRowInBothBuckets(toast.uuid, withRowClaim(toast.uuid, toast.kind, null));
    if (toast.kind !== 'ack' && !anotherCopyClaimStands) patchRowInBothBuckets(toast.uuid, { copyApprovedAt: undefined });
    if (toast.kind) await refetchSelectedDetail(toast.uuid);
  };

  // Neu analysieren — from an open row as always, and from a SETTLED row (decision 2026-10-23):
  // there the reason is mandatory and core withdraws the standing verdict before the re-run, so
  // the row returns to Offen through analysis. The list reaction is the same from either tab: the
  // row leaves Entschieden (if it was there), Offen is re-read, and the pane stays on the row
  // through the transient status (the detail refresh hook keeps it alive) and as the held selection.
  const handleReanalysis = async (request: ReanalysisRequest) => {
    if (!selectedUuid) return;
    const uuid = selectedUuid;
    setError(null);
    setActionLoading(true);
    const result = await tm.requestReanalysis(uuid, request);
    setActionLoading(false);
    if (result.success) {
      // A re-run from the decided face ends the press: the row stays in Offen through analysis.
      press.clear(uuid);
      cancelLeave(uuid);
      setRowMotion(uuid, null);
      setKept(prev => { const cur = prev.get(uuid); if (!cur) return prev; return new Map(prev).set(uuid, { ...cur, row: { ...cur.row, status: 'reanalyzing' }, state: 'returning', reopenedAt: Date.now(), leaving: false }); });
      offenHook.patchRow(uuid, { status: 'reanalyzing' });
      entschiedenHook.removeRow(uuid);
      entschiedenHook.refresh();
      offenHook.refresh();
      setHeldUuid(uuid);
      setShowReanalysisModal(false);
      const detail = await smarttix.getTicket(uuid);
      setSelectedTicket(detail);
    } else {
      setError(result.error || 'Fehler beim Ablehnen');
    }
  };

  const handleDownload = async () => {
    if (!selectedUuid) return;
    try {
      await smarttix.downloadDraft(selectedUuid);
    } catch (err: any) {
      setError(formatBackendError(err, 'Herunterladen des Entwurfs'));
    }
  };

  // ─── Auto-archive ─────────────────────────────────────
  // Eligibility over *loaded* rows (same as ArchivSeite: operates on paginated set).
  // Offen ages from receivedAt (visible column). Entschieden from the decision's own clock — the
  // approval's stamp or the verdict's — the same clock the nightly sweep reads; a row without one
  // is never offered, exactly as the sweep never files it.
  const offenEligibleUuids = autoArchiveDays > 0
    ? offenRows.filter(t => t.status === 'suggestion_ready' && daysSince(t.receivedAt) >= autoArchiveDays).map(t => t.uuid)
    : [];
  const entschiedenEligibleUuids = autoArchiveDays > 0
    ? entschiedenRows.filter(t => { const decided = decidedAtOf(t); return decided !== null && daysSince(decided) >= autoArchiveDays; }).map(t => t.uuid)
    : [];

  const eligibleUuids = activeTab === 'offen' ? offenEligibleUuids : entschiedenEligibleUuids;

  const pickedUuids = activeRows.filter(t => sel.selected.has(t.uuid)).map(t => t.uuid);

  // ⚠ THE EMPTY-SELECTION MEANING DIFFERS PER LEVER, AND THAT ASYMMETRY IS DELIBERATE.
  //   archive + nothing picked -> the auto-archive-eligible tickets (a computed, bounded set)
  //   excel   + nothing picked -> EVERY ticket on the active list; the backend keeps only the ones
  //                              carrying a recognised form and names which it kept.
  // A later reader will be tempted to unify these into one "targets" expression. Do not: on the
  // archive list an empty selection means NOTHING (see ArchivSeite), and here it means EVERYTHING.
  // The two lists answer the same gesture differently on purpose.
  // ⚠ "Alle" IS NO LONGER COUNTED HERE. It used to mean "the form-bearing rows this browser happens to
  // hold", which coincides with the bucket only while everything fits one page — so the label said
  // "aller N" in an authoritative voice about a page-sized N. The count is now a server fact over the
  // same bucket+filters the list uses, and the download resolves the same way, so the number the button
  // promises and the rows the workbook contains come from one source instead of two.
  const formBearingCount = counts.formBearing?.[activeTab] ?? 0;
  // The „Formular" filter bubble's count — the SAME served subset, for the visible tab. Not the `?? 0`
  // above: the bubble reads an unserved count as "no count" (FilterPanel's FormFilterCount).
  const formFilterCount = formFilterCountOf(counts.formBearing, activeTab, activeHook.loading);
  // Read from the server; never mirrored as a local constant. Infinity when absent so an older backend
  // simply never pre-refuses.
  const formBatchCap = counts.formBatchCap ?? Infinity;
  const formExtractionOff = counts.formExtractionEnabled === false;

  // Selection mode means the pick, 0 included: with nothing ticked the press does nothing (the line
  // says „0 ausgewählte Tickets"). It no longer falls back to the older-tickets group — that is the
  // steady action of the page OUTSIDE selection mode (Auto button, 2026-10-31).
  const autoTargetUuids = sel.purpose !== null
    ? pickedUuids                                    // excel: "alle" is a server-side selection, not a list
    : eligibleUuids;

  // Would a click do anything, and if not, WHY — one decision, so the tooltip and the refusal toast
  // cannot tell the clerk two different stories. See utils/excelBatchAction.
  const excelAction = describeExcelBatchAction({
    pickedCount: pickedUuids.length,
    formBearingCount,
    formBatchCap,
    formExtractionOff,
    countsLoading: activeHook.loading,
    // A failed fetch is not a measured zero — see the helper's countsFailed branch.
    countsFailed: activeHook.error !== null,
  });
  const excelHasAction = excelAction.canAct;

  const autoHasAction = autoTargetUuids.length > 0;

  // What one press does right now — the line's sentence, the button's face, whether it acts. One
  // table (utils/autoAction) shared by the line, the button's name and the click.
  const autoAction = antwortenAutoAction({
    purpose: sel.purpose,
    picked: pickedUuids.length,
    tab: activeTab,
    eligible: eligibleUuids.length,
    days: autoArchiveDays,
    excel: { canAct: excelHasAction, refusal: excelAction.refusal, formBearingCount, countsLoading: activeHook.loading },
  });
  // The Formular-Excel download lives HERE because this is the page a Sachbearbeiter works tickets on.
  // It was first built on the Archiv page, which has the same selection mechanism and was therefore the
  // easy place to put it — and the wrong one: a feature rendered where nobody looks is not delivered.
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const handleDownloadXlsx = async () => {
    if (xlsxBusy) return;
    // The reason, not a generic sentence: a clerk blocked by a tenant lever or by the batch cap used to
    // be told "no selection", which is a statement about something they had done nothing wrong with.
    if (!excelHasAction) { setInfoToast(excelAction.refusal!); return; }

    // Two gestures. An explicit pick sends exactly those uuids; an empty selection sends the VIEW, and
    // the server resolves which rows that is — the client no longer enumerates anything for "alle".
    // Built from the SAME `filters` memo the list query uses, not from re-reading the individual filter
    // states: that is what guarantees the download resolves the view the clerk is actually looking at.
    // Rebuilding the filter set by hand here is exactly how the two would drift apart.
    const target = pickedUuids.length > 0
      ? { uuids: pickedUuids }
      : {
          selection: {
            bucket: activeTab,
            sort,
            dir,
            search: filters.search || undefined,
            primaryAxis: filters.primaryAxis || undefined,
            secondaryAxis: filters.secondaryAxis && filters.secondaryAxis.length > 0 ? filters.secondaryAxis : undefined,
            from: filters.from || undefined,
            to: filters.to || undefined,
            uploadedBy: filters.uploadedBy || undefined,
            // part 7, 2026-10-11 (controller ruling): EVERY list filter rides the selection — the
            // attribute and organisation filters (a pre-existing gap: an organisation-filtered view
            // pulled rows that were not on screen) and the pill filters. Arrays in the body.
            sparte: filters.sparte && filters.sparte.length > 0 ? filters.sparte : undefined,
            senderKind: filters.senderKind && filters.senderKind.length > 0 ? filters.senderKind : undefined,
            senderOrganisationId: filters.senderOrganisationId && filters.senderOrganisationId.length > 0 ? filters.senderOrganisationId : undefined,
            flag: filters.flag,
            searchPrimary: filters.searchPrimary,
            searchSecondary: filters.searchSecondary,
            // The „Formular" facet (part 7 fix loop, 2026-10-12): export = screen. On a list
            // filtered to form-bearing rows the workbook selects exactly those rows.
            hasForm: filters.hasForm,
          },
        };

    setXlsxBusy(true);
    try {
      const res = await smarttix.downloadFormRowsXlsx(target);
      if (res.error || !res.blob) { setInfoToast(res.error || 'Excel-Download fehlgeschlagen.'); return; }

      // ⚠ The gap between MATCHED and DELIVERED is the only detector the clerk has, and it catches two
      // different things at once: rows selected whose stored extraction could not be read, and the view
      // having moved between the count on the button and the click. Both were previously invisible.
      const skipped = res.matched - res.delivered;
      if (skipped > 0) {
        setInfoToast(`${res.delivered} von ${res.matched} Vorgaengen konnten uebernommen werden. `
          + `${skipped} enthalten kein lesbares Formular.`);
      }

      const url = URL.createObjectURL(res.blob);
      const a = document.createElement('a');
      a.href = url;
      // The name comes from the client, which composed it from the rows the server actually
      // included. Hardcoding it here once made the rename inert: the api returned the right
      // name and the anchor ignored it.
      a.download = res.fileName ?? 'Notizblatt.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Released on the next tick: revoking synchronously can cancel the download before the browser
      // has read the object.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } finally {
      setXlsxBusy(false);
    }
  };

  const handleAutoArchive = async () => {
    if (autoTargetUuids.length === 0 || autoSubmitting) return;
    setAutoSubmitting(true);
    const uuids = autoTargetUuids.slice();
    setRemovingUuids(new Set(uuids));
    await new Promise(r => setTimeout(r, 200 + Math.min(40 * uuids.length, 2000)));
    try {
      const res = await smarttix.bulkArchive(uuids);
      if (res.error) {
        setError(res.error);
        setRemovingUuids(new Set());
      } else {
        setUndoToast({ count: res.archived, verb: 'archiviert', uuids });
        announceTicketMutated('*');
        sel.exitMode();
        if (activeTab === 'offen') offenHook.reset();
        else entschiedenHook.reset();
        setRemovingUuids(new Set());
      }
    } finally {
      setAutoSubmitting(false);
    }
  };

  // ONE button, one press; the active lever decides the verb. A quiet button never calls this.
  const handleAutoPress = () => {
    if (autoSubmitting) return;
    if (sel.purpose === 'excel') { handleDownloadXlsx(); return; }
    if (autoHasAction) handleAutoArchive();
  };

  const handleUndo = async () => {
    if (!undoToast) return;
    const toastUuids = undoToast.uuids;
    setUndoToast(null);
    await smarttix.bulkUnarchive(toastUuids);
    announceTicketMutated('*');
    offenHook.reset();
    entschiedenHook.reset();
  };

  // ─── Layout ───────────────────────────────────────────
  const { flip } = useListDetailFlip();
  // Flip-OFF static split: list −30px / detail +30px (60px total spread); ratio object untouched so flip-ON 2:1→1:2 morph is unaffected.
  // Flip-ON: 2:1→1:2 morph driven by selection (same behaviour as ArchivSeite).
  const ratio = flip ? listDetailFlex(selectedUuid) : { list: 1, detail: 1 };
  // Flip-off basis offsets: applied only in non-compact non-flip layout.
  const defaultBasis = flip ? {} : { list: 'calc(50% - 30px)', detail: 'calc(50% + 30px)' };
  const listRef = useRef<HTMLDivElement>(null);
  // The detail pane's own focus target — see the region it is attached to at the pane below.
  const detailRegionRef = useRef<HTMLDivElement>(null);
  const closeDetail = () => {
    const row = selectedUuid ? listRef.current?.querySelector<HTMLElement>(`[data-uuid="${selectedUuid}"]`) : null;
    setSelectedUuid(null);
    (row ?? listRef.current)?.focus();
  };

  const distinctUploaders = useMemo(() => {
    const emails = new Set([...offenRows, ...entschiedenRows].map(t => t.uploadedByEmail).filter(Boolean) as string[]);
    return Array.from(emails).sort();
  }, [offenRows, entschiedenRows]);

  // ─── Render ───────────────────────────────────────────

  const renderRow = (t: typeof offenRows[0], idx: number, isOffenTab: boolean) => {
    const isSelected = selectedUuid === t.uuid;
    const isPicked = sel.selected.has(t.uuid);
    const isRemoving = removingUuids.has(t.uuid);
    const isArriving = t.uuid === arrivalUuid;
    // A row the clerk just decided (the press, 2026-10-28): it keeps its place and its selection,
    // the verdict's glyph where the unread dot sat, its text greyed. Offen only: the same row is
    // already listed in Entschieden, where it is an ordinary row.
    const keptRow = isOffenTab ? kept.get(t.uuid) : undefined;
    const decided = keptRow?.state === 'held' ? keptRow : undefined;
    const rowMotion = isOffenTab ? motion.get(t.uuid) : undefined;
    const showUnreadDot = isOffenTab && !decided && !isRead(t.uuid);
    // The leave (fade, then close) and the return (open back) — the row's own box animates; the
    // rows below follow its height.
    const motionStyle: React.CSSProperties = !rowMotion ? {}
      : rowMotion.phase === 'fade' ? { opacity: 0, height: rowMotion.h, overflow: 'hidden', transition: `opacity ${LEAVE_FADE_MS}ms linear` }
        : rowMotion.phase === 'close' ? { opacity: 0, height: 0, overflow: 'hidden', borderBottomWidth: 0, transition: `height ${LEAVE_CLOSE_MS}ms ease-out` }
          : rowMotion.phase === 'from' ? { opacity: 0, height: 0, overflow: 'hidden', borderBottomWidth: 0, transition: 'none' }
            : { opacity: 1, height: rowMotion.h, overflow: 'hidden', transition: `height ${LEAVE_CLOSE_MS}ms ease-out, opacity ${LEAVE_CLOSE_MS}ms ease-out` };
    // part 7 fix loop, 2026-10-12: the unclassified value's band is the slate tint (primaryBandStyle).
    // The OPEN row frames its band in the band's own ink (primaryBandStyle `open`; operator pick C,
    // 2026-10-19) — the unclassified slate tint no longer vanishes into the selection fill.
    const stripPaint: React.CSSProperties = t.status === 'pipeline_error'
      ? { backgroundColor: theme.statusStrip.error }
      : primaryBandStyle(cm, t.primaryAxis, theme.statusStrip.unclassified, isSelected);


    return (
      <div
        key={t.uuid}
        data-uuid={t.uuid}
        className={isRemoving ? 'stx-archiving' : undefined}
        tabIndex={0}
        aria-label={`Ticket ${t.subject ?? t.uuid}`}
        onClick={() => {
          if (rowMotion) return;
          if (sel.mode) { sel.toggle(t.uuid); return; }
          if (isOffenTab) markRead(t.uuid);
          setSelectedUuid(isSelected ? null : t.uuid);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (rowMotion) return;
            if (sel.mode) { sel.toggle(t.uuid); return; }
            if (isOffenTab) markRead(t.uuid);
            if (isSelected) {
              // Toggling the open row shut is the return leg — focus stays on (or comes back to)
              // the row, so the keyboard user is left standing in the list they were walking.
              const row = e.currentTarget;
              setSelectedUuid(null);
              row.focus();
              return;
            }
            setSelectedUuid(t.uuid);
            // Focus follows the ACTIVATION, and only the keyboard one: this branch is onKeyDown,
            // never onClick, because taking focus off a mouse user's pointer is the opposite of
            // helpful. Deferred a frame so the pane has rendered the region before we focus it.
            requestAnimationFrame(() => detailRegionRef.current?.focus());
          }
        }}
        style={{
          display: 'flex',
          cursor: 'pointer',
          fontFamily: theme.font.family,
          backgroundColor: isPicked ? theme.colors.bgLight : (isSelected ? theme.colors.bgLight : 'transparent'),
          borderLeft: isPicked ? `4px solid ${theme.colors.navy}` : '4px solid transparent',
          borderBottom: `1px solid ${theme.colors.borderLight}`,
          transition: 'background-color 0.15s ease, opacity 0.3s ease',
          ...motionStyle,
          ...(isArriving ? { animation: 'stx-arrivalPulse 1.5s ease-out' } : {}),
          ...(isRemoving ? { transitionDelay: `${idx * 40}ms` } : {}),
        }}
      >
        {/* Category color strip (wim vocabulary v7, 2026-09-13) — no title= tooltip at all when
            there's no category yet (nothing to name); the styled InfoTooltip only wraps the strip
            once t.primaryAxis is set.
            The FACE inside it is HandlingStripFace (HandlingStripFace.tsx), shared with the ledger and the
            dashboard's Verlauf: served glyph, else served Kürzel, else colour only — one tier order
            for all three lists. The box stays this call site's, so no face can re-flow the row.
            Its WIDTH comes from theme.stripWidth keyed on the MODE (operator ruling 2026-09-16):
            full width when the strip carries a face, half when it is a bare colour band. Keyed on
            the mode and never on this row's data, so a classification landing cannot move the row.
            The hover sits ONLY on the element that carries the FACE (operator eyeball round 2,
            2026-09-17, C3.2): in strip_abbr the strip itself is the face and keeps the hover; in
            strip_label the strip is a bare colour band (nothing to name) and the line-2
            CategoryBadge is the one that already carries this exact hover (same
            handlingTooltipText helper) — wrapping the strip too would double both the hover bubble
            and the tab stop on the same row. */}
        {t.primaryAxis && cm.rowLayout === 'strip_abbr' ? (
          <InfoTooltip text={handlingTooltipText(cm, t.primaryAxis)}>
            <div data-testid="handling-strip" style={{
              width: theme.stripWidth[cm.rowLayout], flexShrink: 0,
              ...stripPaint,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <HandlingStripFace value={t.primaryAxis} layout={cm.rowLayout} />
            </div>
          </InfoTooltip>
        ) : (
          <div data-testid="handling-strip" style={{
            width: theme.stripWidth[cm.rowLayout], flexShrink: 0,
            ...stripPaint,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <HandlingStripFace value={t.primaryAxis ?? null} layout={cm.rowLayout} />
          </div>
        )}

        {/* Content */}
        <div style={{
          flex: 1, minWidth: 0, position: 'relative',
          // Why: right-padding pre-allocates dot space on Offen rows to prevent layout
          // shift when the dot appears/disappears (UI Layout Stability rule).
          padding: `6px ${theme.spacing.sm + 2}px 6px 12px`,
        }}>
          {/* The verdict's glyph in the dot's place: a check for the yes, the instead-arrow for the
              no — the same two keys the decision badge wears. Same box as the dot, so the row's
              geometry does not move when one replaces the other. */}
          {decided && (
            <span
              data-testid={`decided-glyph-${decided.verdict}`}
              aria-hidden="true"
              style={{
                position: 'absolute', top: theme.spacing.sm - 3, right: theme.spacing.sm - 3,
                display: 'inline-flex', width: 14, height: 14, alignItems: 'center', justifyContent: 'center',
                color: theme.colors.navy, animation: 'stx-fadeIn 0.15s ease',
              }}
            >
              {decided.verdict === 'approved' ? <CheckIcon stamp size={14} color={theme.colors.navy} /> : <InsteadIcon size={14} color={theme.colors.navy} />}
            </span>
          )}
          {/* Unread dot — Offen rows only; DB-backed via useReadState (#6).
              Space pre-allocated via paddingRight; dot fades in/out (Layout Stability). */}
          {showUnreadDot && (
            <div
              data-testid="unread-dot"
              aria-hidden="true"
              style={{
                position: 'absolute', top: theme.spacing.sm, right: theme.spacing.sm,
                width: 8, height: 8, borderRadius: '50%',
                backgroundColor: theme.colors.navy,
                animation: 'stx-fadeIn 0.2s ease',
              }}
            />
          )}
          {/* Row 1: Subject · the ONE CLOCK (part 7 fix loop, 2026-10-12, operator ruling): the
              relative age right beside the unread dot, its hover the full arrival stamp
              (ArrivalAge). The arrival is stated nowhere else on the row — the subject carries no
              hover any more. ALWAYS the arrival, on both tabs: the Entschieden tab's decision reads
              as row 3's badge, and the auto-archive aging from `approvedAt` is a different question
              from what the row SAYS. The dot's space stays reserved by the tab (paddingRight),
              never by the dot's own state, and the age has a fixed min-width — so neither the dot
              appearing nor the age changing moves the subject. */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, paddingRight: isOffenTab ? 16 : 0 }}>
            <div style={{
              flex: 1, minWidth: 0,
              fontSize: 13, fontWeight: 500, color: decided ? theme.colors.textSecondary : theme.colors.navy,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', transition: 'color 0.15s ease',
            }}>
              {t.subject || '(Kein Betreff)'}
            </div>
            <ArrivalAge receivedAt={t.receivedAt} createdAt={t.createdAt} flags={t.flags} />
          </div>

          {/* Rows 2+3: shared meta — category(strip_label)·Anliegen·+N·Anhang // Marktpartner·past tense·↻N·Kopiert·bearbeitet·Eskalation */}
          <TicketRowMeta ticket={t} maxSecondaryAxisVisible={SECONDARY_AXIS_MAX_VISIBLE.responses} attachmentsCountInline={attachmentsCountInline} />
        </div>
      </div>
    );
  };

  const offenCount = counts.offen;
  const entschiedenCount = counts.entschieden;

  return (
    <div style={{ fontFamily: theme.font.family }}>
      {/* Title row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: theme.spacing.md,
        paddingBottom: theme.spacing.md,
        borderBottom: `1px solid ${theme.colors.borderLight}`,
        marginBottom: theme.spacing.lg,
        flexWrap: 'wrap',
      }}>
        <h1 style={{
          fontSize: 22, fontWeight: 600, color: theme.colors.navy,
          margin: 0, flex: 1, minWidth: 200,
        }}>
          {activeHook.loading && activeRows.length === 0
            ? 'Antworten'
            : (() => {
                const n = activeTab === 'offen' ? offenCount : entschiedenCount;
                if (n > 0) return `${n} Ticket${n !== 1 ? 's' : ''} ${activeTab === 'offen' ? 'zur Prüfung' : 'entschieden'}`;
                return 'Antworten';
              })()}
        </h1>
        <AutoBar
          page="antworten"
          action={autoAction}
          onPress={handleAutoPress}
          onClear={sel.clear}
          busy={autoSubmitting || xlsxBusy}
        >
          {activeRows.length > 0 && (
            <Lever on={sel.purpose === 'archive'} label={sel.purpose === 'archive' ? 'Auswahlmodus beenden' : 'Auswahlmodus starten'} onClick={() => sel.setPurpose('archive')}>
              <SelectionToggleIcon size={14} active={sel.purpose === 'archive'} />
            </Lever>
          )}
          {/* The form-to-excel LEVER, sibling of selection mode: it enters selection mode too, and only
              the verb the Auto button then carries differs. Gated on the tenant capability FIRST (the
              schema context, the rows' Formular pill's single source): a role that does not own it never
              sees it exists. */}
          {cm.formExtractionEnabled && activeRows.length > 0 && (
            <Lever on={sel.purpose === 'excel'} label={sel.purpose === 'excel' ? 'Formular-Excel-Auswahl beenden' : 'Formularzeilen als Excel auswählen'} onClick={() => sel.setPurpose('excel')}>
              <SpreadsheetRowIcon size={14} active={sel.purpose === 'excel'} />
            </Lever>
          )}
        </AutoBar>
      </div>

      {/* Main layout: list + detail */}
      <div style={{
        display: 'flex', gap: theme.spacing.lg,
        flexDirection: isCompact ? 'column' : 'row',
        ...(isCompact ? {} : { height: 'calc(100vh - 200px)', minHeight: 400 }),
      }}>
        {/* Left: ticket list */}
        <BentoCard
          title="Tickets"
          style={{
            display: 'flex', flexDirection: 'column', minWidth: 0,
            flex: isCompact ? 1 : ratio.list,
            ...(isCompact ? { maxHeight: 500 } : {
              flexBasis: defaultBasis.list,
              transition: MORPH_TRANSITION,
            }),
          }}
          contentStyle={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, padding: 0 }}
        >
          <FilterPanel
            search={search}
            onSearchChange={setSearch}
            open={filterOpen}
            onOpenChange={setFilterOpen}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={setCategoryFilter}
            reasonCodeFilter={reasonCodeFilter}
            onReasonCodeFilterChange={setReasonCodeFilter}
            attributeFilter={attributeFilter}
            onAttributeFilterChange={(name, values) =>
              setAttributeFilter(prev => withAttributeValues(prev, name, values))}
            senderFilter={senderFilter}
            onSenderFilterChange={setSenderFilter}
            flagFilter={flagFilter}
            onFlagFilterChange={setFlagFilter}
            hasFormFilter={hasFormFilter}
            onHasFormFilterChange={setHasFormFilter}
            formFilterCount={formFilterCount}
            facets={facets}
            facetsUnsupported={facetsUnsupported}
            uploaders={distinctUploaders}
            filterUser={filterUser}
            onFilterUserChange={setFilterUser}
            from={dateRange.from}
            to={dateRange.to}
            onDateRangeChange={setDateRange}
            dateRangeLabel="Eingang"
            onClearAll={() => {
              setSearch(''); setCategoryFilter(null);
              setReasonCodeFilter([]); setFilterUser(''); setDateRange({ from: null, to: null });
              setAttributeFilter({}); setSenderFilter([]);
              setFlagFilter([]); setHasFormFilter(false);
            }}
            hasActiveFilters={hasActiveFilters}
          />

          {/* Tabs: Offen / Entschieden */}
          <div style={{
            display: 'flex',
            borderBottom: `1px solid ${theme.colors.borderLight}`,
            marginBottom: theme.spacing.sm,
            userSelect: 'none',
          }}>
            {([
              { key: 'offen' as const, count: offenCount },
              { key: 'entschieden' as const, count: entschiedenCount },
            ]).map(({ key, count }) => {
              const active = activeTab === key;
              return (
                <button key={key} onClick={() => setActiveTab(key)}
                  title={cm.configLoading ? undefined : (cm.face('bucket', key) ?? key)}
                  style={{
                  flex: 1, minWidth: 0, position: 'relative',
                  display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4,
                  padding: '8px 0', background: 'none',
                  border: 'none', borderBottom: `2px solid ${active ? theme.colors.navy : 'transparent'}`,
                  fontSize: 13, fontWeight: active ? 600 : 400,
                  color: active ? theme.colors.navy : theme.colors.textSecondary,
                  cursor: 'pointer', fontFamily: theme.font.family,
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    <ServedFace family="bucket" faceKey={key} nullAs="key" width={ACTIVE_TAB_SHIMMER_WIDTH[key]} height={16} render={w => w} />
                  </span>
                  {/* The count cross-fades when it ticks (as a decided row closes out of Offen). */}
                  <span key={`count-${count}`} data-testid={`tab-count-${key}`} style={{ flexShrink: 0, animation: 'stx-fadeIn 0.15s ease' }}>({count})</span>
                  {key === 'entschieden' && pulseKey > 0 && (
                    <span key={`pulse-${pulseKey}`} aria-hidden="true" data-testid="entschieden-pulse" style={{
                      position: 'absolute', left: '30%', right: '30%', bottom: -2, height: 2,
                      backgroundColor: theme.colors.navy, animation: 'stx-tabPulse 0.5s ease-out forwards', pointerEvents: 'none',
                    }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Sort field dropdown + direction flip */}
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: `${theme.spacing.xs}px ${theme.spacing.md}px ${theme.spacing.xs}px ${4 + theme.spacing.sm}px`,
            fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary,
            textTransform: 'uppercase', letterSpacing: 0.5,
            borderBottom: `1px solid ${theme.colors.borderLight}`,
            userSelect: 'none', gap: 4,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>Betreff / {cm.categoryNoun}</div>
            <select
              id="antworten-sort"
              name="antworten-sort"
              value={sort}
              onChange={e => setSort(e.target.value as ActiveSort)}
              aria-label="Sortierspalte"
              style={{
                flexShrink: 0, fontSize: 11, fontWeight: 600,
                color: theme.colors.navy, background: theme.colors.white,
                border: `1px solid ${theme.colors.borderLight}`,
                borderRadius: theme.borderRadius.sm,
                padding: '2px 4px', cursor: 'pointer',
                fontFamily: theme.font.family,
                outline: 'none', textTransform: 'uppercase', letterSpacing: 0.3,
              }}
            >
              {cm.schemaActive && <option value="zone">Zone</option>}
              <option value="updated">Aktualisiert</option>
              <option value="uploaded">Hochgeladen</option>
              <option value="received">E-Mail-Eingang</option>
              <option value="rejections">Neuanalysen</option>
            </select>
            <button
              onClick={() => setDir(d => d === 'desc' ? 'asc' : 'desc')}
              aria-label={dir === 'desc' ? 'Absteigende Reihenfolge — klicken für aufsteigend' : 'Aufsteigende Reihenfolge — klicken für absteigend'}
              title={dir === 'desc' ? 'Absteigende Reihenfolge' : 'Aufsteigende Reihenfolge'}
              style={{
                flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, padding: 0,
                color: theme.colors.navy, background: 'none',
                border: `1px solid ${theme.colors.borderLight}`,
                borderRadius: theme.borderRadius.sm,
                cursor: 'pointer',
              }}
            >
              <SortDirectionIcon size={13} color={theme.colors.navy} dir={dir} />
            </button>
          </div>

          {/* Row list */}
          <div ref={listRef} tabIndex={-1} className="stx-no-scrollbar" style={{ flex: 1, overflowY: 'auto', outline: 'none' }}>
            {activeHook.loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
                Lade Tickets...
              </div>
            ) : activeHook.error ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.error, fontSize: 13 }}>
                {activeHook.error}
                <div style={{ marginTop: theme.spacing.sm }}>
                  <button
                    onClick={() => activeHook.reset()}
                    style={{
                      padding: '6px 14px', fontSize: 13, fontWeight: 500,
                      fontFamily: theme.font.family,
                      background: 'transparent', color: theme.colors.navy,
                      border: `1px solid ${theme.colors.borderLight}`,
                      borderRadius: theme.borderRadius.sm, cursor: 'pointer',
                    }}
                  >Erneut versuchen</button>
                </div>
              </div>
            ) : activeRows.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
                {hasActiveFilters ? 'Keine Treffer' : 'Keine Tickets vorhanden'}
              </div>
            ) : (
              activeRows.map((t, idx) => renderRow(t, idx, activeTab === 'offen'))
            )}

            {/* Infinite-scroll sentinel — only mounted for active tab */}
            {!activeHook.loading && !activeHook.error && activeRows.length > 0 && (
              <div ref={activeHook.sentinelRef} aria-hidden="true" style={{ height: 1 }} />
            )}
            {activeHook.loadingMore && (
              <div style={{ padding: 16, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 12 }}>
                Lade weitere…
              </div>
            )}
          </div>
        </BentoCard>

        {/* Right: ticket detail */}
        <BentoCard
          title="Details"
          headerAction={selectedUuid ? (
            <div style={{ display: 'flex', gap: 4, animation: 'stx-fadeIn 0.2s ease' }}>
              <HeaderIconButton label="In eigenem Fenster öffnen" onClick={popoutDetail}><PopoutIcon size={15} /></HeaderIconButton>
              <HeaderIconButton label="Auswahl aufheben" onClick={closeDetail}><CircleIcon size={16} /></HeaderIconButton>
            </div>
          ) : undefined}
          style={{
            display: 'flex', flexDirection: 'column', minWidth: 0,
            ...(isCompact
              ? { width: '100%', flexShrink: 0, minHeight: 300 }
              : { flex: ratio.detail, flexBasis: defaultBasis.detail, transition: MORPH_TRANSITION }),
          }}
          contentStyle={{ padding: theme.spacing.lg, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}
        >
          {/* THE focus target for a keyboard activation (operator ruling 2026-09-16). A landmark
              region rather than a bare div: the clerk who just pressed Enter on a row is told what
              they landed in, and the same element is where Tab continues from — forward into the
              detail's own controls instead of onward down the list.
              tabIndex -1 keeps it OUT of the tab order (it is a destination, not a stop), and the
              wrapper is mounted in EVERY state — placeholder, loading, detail — so the ref is
              stable and nothing about the pane's geometry depends on which state is showing. */}
          <div
            ref={detailRegionRef}
            role="region"
            aria-label="Ticketdetails"
            tabIndex={-1}
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', outline: 'none' }}
          >
          {!selectedUuid ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.colors.textSecondary, fontSize: 15,
            }}>
              Ticket auswählen um Details anzuzeigen
            </div>
          ) : !selectedTicket && !detailLoading ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.colors.textSecondary, fontSize: 15,
            }}>
              Ticket nicht gefunden
            </div>
          ) : selectedTicket ? (
            <div
              data-testid="detail-pane-body"
              aria-busy={detailStale || undefined}
              style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', opacity: detailStale ? 0.45 : 1, transition: 'opacity 0.15s ease', pointerEvents: detailStale ? 'none' : undefined }}
            >
            <TicketDetailComponent
              onInfo={setInfoToast}
              ticket={selectedTicket}
              loading={detailLoading}
              actionLoading={actionLoading}
              error={error}
              onClearError={() => setError(null)}
              onApprove={() => { void press.approve(selectedTicket); }}
              onDecline={instead => press.decline(selectedTicket, instead)}
              onApproveKind={kind => { void press.approveKind(selectedTicket, kind); }}
              afterPress={afterPress}
              pressMessage={press.message}
              onDismissPressMessage={press.dismissMessage}
              pressKeys={!showReanalysisModal && !detailStale}
              onCopyApprove={handleCopyApprove}
              onCopyDraft={handleCopyDraft}
              onReject={() => setShowReanalysisModal(true)}
              onDownload={handleDownload}
              onGoToUploads={STATUS_GROUPS.eingereicht.includes(selectedTicket.status) && !tm.processingUuids.has(selectedTicket.uuid) ? () => onNavigate('uploads', { ticket: selectedTicket.uuid }) : undefined}
              onReprocess={selectedTicket.status === 'pipeline_error' ? async () => {
                setActionLoading(true);
                setError(null);
                const result = await tm.processTicket(selectedTicket.uuid);
                setActionLoading(false);
                if (result.success) {
                  offenHook.patchRow(selectedTicket.uuid, { status: 'reanalyzing' });
                  const detail = await smarttix.getTicket(selectedTicket.uuid);
                  setSelectedTicket(detail);
                } else {
                  setError(result.error || 'Fehler beim erneuten Verarbeiten');
                }
              } : undefined}
              onArchive={isSettledStatus(selectedTicket.status) || selectedTicket.status === 'pipeline_error' ? async () => {
                setActionLoading(true);
                setError(null);
                const result = await tm.archiveTicket(selectedTicket.uuid);
                setActionLoading(false);
                if (result.success) {
                  const filed = selectedTicket.uuid;
                  press.clear(filed);
                  cancelLeave(filed);
                  setRowMotion(filed, null);
                  departedRef.current.delete(filed);
                  setKept(prev => { if (!prev.has(filed)) return prev; const next = new Map(prev); next.delete(filed); return next; });
                  offenHook.removeRow(filed);
                  entschiedenHook.removeRow(filed);
                  setSelectedUuid(null);
                  setSelectedTicket(null);
                } else {
                  setError(result.error || 'Fehler beim Archivieren');
                }
              } : undefined}
              piiIgnoreList={piiIgnoreList}
              onPiiIgnore={handlePiiIgnore}
              onSuggestionEdited={() => refetchSelectedDetail(selectedTicket.uuid)}
              onActionError={setError}
              onStaleSuggestion={() => { void handleStale(selectedTicket.uuid); }}
              simulateSystemData={simulateSystemData}
              showSourceChannel={showSourceChannel}
              attachmentsCountInline={attachmentsCountInline}
            />
            </div>
          ) : (
            <TicketDetailComponent
              ticket={null as any}
              loading={true}
              actionLoading={false}
              error={null}
              onClearError={() => {}}
              onApprove={() => {}}
              onReject={() => {}}
              onDownload={() => {}}
            />
          )}
          </div>
        </BentoCard>
      </div>

      <RueckgaengigToast
        visible={!!undoToast}
        showKey={undoToast ? undoToast.uuids.join(',') : undefined}
        message={undoToast ? (
          <><CountUpNumber target={undoToast.count} /> Ticket{undoToast.count !== 1 ? 's' : ''} {undoToast.verb}</>
        ) : ''}
        onUndo={handleUndo}
        onDismiss={() => setUndoToast(null)}
      />

      <RueckgaengigToast
        visible={!!infoToast}
        message={infoToast ?? ''}
        onDismiss={() => setInfoToast(null)}
      />

      <RueckgaengigToast
        visible={!!copyToast}
        showKey={copyToast?.uuid}
        message={copyToastMessage(copyToast?.kind, cm.face)}
        durationMs={COPY_TOAST_MS}
        onUndo={handleUndoCopy}
        onDismiss={() => setCopyToast(null)}
      />

      {showReanalysisModal && selectedTicket && (
        <ReanalysisModal
          ticketUuid={selectedTicket.uuid}
          currentCategory={selectedTicket.primaryAxis}
          reanalysisCount={selectedTicket.reanalysisCount}
          reanalysisBlocked={selectedTicket.reanalysisBlocked}
          settled={isSettledStatus(selectedTicket.status)}
          attachments={selectedTicket.attachments}
          ocrDecisions={selectedTicket.ocrDecisions}
          onSubmit={handleReanalysis}
          onUnblock={async () => {
            setActionLoading(true);
            const result = await tm.unblockReanalysis(selectedTicket.uuid);
            setActionLoading(false);
            if (result.success) {
              offenHook.patchRow(selectedTicket.uuid, { reanalysisBlocked: false });
              const detail = await smarttix.getTicket(selectedTicket.uuid);
              setSelectedTicket(detail);
            } else {
              setError(result.error || 'Fehler beim Entsperren');
            }
          }}
          onClose={() => setShowReanalysisModal(false)}
          isSubmitting={actionLoading}
          isAdmin={!!(userRoles?.isCustomerAdmin || userRoles?.isAdmin)}
          // Same value the detail pane above receives, from the same App-level /config read: the
          // modal opens ON TOP of that pane, so a dropped prop here puts two different attachment
          // counts on screen at the same time for the same ticket.
          attachmentsCountInline={attachmentsCountInline}
        />
      )}
    </div>
  );
};

export default AntwortenSeite;
